// LLM Configuration Page (Administration)
import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '@/lib/auth-context';
import { AppLayout } from '@/components/layout/AppLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Sparkles,
  Save,
  Loader2,
  Key,
  CheckCircle,
  AlertCircle,
  Eye,
  EyeOff
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const providers = [
  {
    value: 'openai',
    label: 'OpenAI',
    models: [
      { value: 'gpt-5.2', label: 'GPT-5.2 (Recommandé)' },
      { value: 'gpt-5.1', label: 'GPT-5.1' },
      { value: 'gpt-4o', label: 'GPT-4o' },
      { value: 'gpt-4.1', label: 'GPT-4.1' }
    ]
  },
  {
    value: 'anthropic',
    label: 'Anthropic (Claude)',
    models: [
      { value: 'claude-sonnet-4-5-20250929', label: 'Claude Sonnet 4.5 (Recommandé)' },
      { value: 'claude-4-sonnet-20250514', label: 'Claude 4 Sonnet' },
      { value: 'claude-opus-4-5-20251101', label: 'Claude Opus 4.5' },
      { value: 'claude-haiku-4-5-20251001', label: 'Claude Haiku 4.5' }
    ]
  },
  {
    value: 'gemini',
    label: 'Google Gemini',
    models: [
      { value: 'gemini-3-flash-preview', label: 'Gemini 3 Flash (Recommandé)' },
      { value: 'gemini-3-pro-preview', label: 'Gemini 3 Pro' },
      { value: 'gemini-2.5-pro', label: 'Gemini 2.5 Pro' },
      { value: 'gemini-2.5-flash', label: 'Gemini 2.5 Flash' }
    ]
  }
];

export default function LLMConfigPage() {
  const { workspaceId: urlWorkspaceId } = useParams();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [workspaceId, setWorkspaceId] = useState(urlWorkspaceId);

  const [config, setConfig] = useState({
    provider: 'openai',
    model: 'gpt-5.2',
    api_key: '',
    
  });

  const [hasCustomKey, setHasCustomKey] = useState(false);

  const token = localStorage.getItem('token');

  useEffect(() => {
    const init = async () => {
      let wsId = urlWorkspaceId || user?.current_workspace_id || localStorage.getItem('current_workspace_id');
      
      if (!wsId) {
        try {
          const response = await fetch(`${API_URL}/api/workspaces`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const workspaces = await response.json();
          if (workspaces?.length > 0) {
            wsId = workspaces[0].id;
            localStorage.setItem('current_workspace_id', wsId);
          }
        } catch (error) {
          console.error('Failed to fetch workspaces:', error);
        }
      }
      
      setWorkspaceId(wsId);
    };
    
    init();
  }, [urlWorkspaceId, user]);

  useEffect(() => {
    if (workspaceId) {
      loadConfig();
    }
  }, [workspaceId]);

  const loadConfig = async () => {
    try {
      const response = await fetch(
        `${API_URL}/api/training/llm-config?workspace_id=${workspaceId}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.ok) {
        const data = await response.json();
        setConfig(prev => ({
          ...prev,
          provider: data.provider,
          model: data.model
        }));
        setHasCustomKey(data.has_custom_key);
      }
    } catch (error) {
      console.error('Failed to load config:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        provider: config.provider,
        model: config.model,
        
      };

      if (config.api_key ) {
        payload.api_key = config.api_key;
      }

      const response = await fetch(
        `${API_URL}/api/training/llm-config?workspace_id=${workspaceId}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(payload)
        }
      );

      if (response.ok) {
        toast.success('Configuration sauvegardée');
        loadConfig();
      } else {
        throw new Error('Save failed');
      }
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const getAvailableModels = () => {
    const provider = providers.find(p => p.value === config.provider);
    return provider?.models || [];
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <div className="p-6 max-w-2xl mx-auto space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Sparkles className="h-6 w-6 text-purple-500" />
            Configuration IA
          </h1>
          <p className="text-muted-foreground">
            Configurez le modèle de langage utilisé pour la génération de contenu
          </p>
        </div>

        {/* Provider Selection */}
        <Card>
          <CardHeader>
            <CardTitle>Fournisseur et Modèle</CardTitle>
            <CardDescription>
              Choisissez le service d'IA à utiliser pour la génération de contenu
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Fournisseur</Label>
              <Select
                value={config.provider}
                onValueChange={(value) => {
                  const provider = providers.find(p => p.value === value);
                  setConfig(prev => ({
                    ...prev,
                    provider: value,
                    model: provider?.models[0]?.value || ''
                  }));
                }}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {providers.map(p => (
                    <SelectItem key={p.value} value={p.value}>
                      {p.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Modèle</Label>
              <Select
                value={config.model}
                onValueChange={(value) => setConfig(prev => ({ ...prev, model: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {getAvailableModels().map(m => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* API Key Configuration */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              Clé API
            </CardTitle>
            <CardDescription>
              Configurez la clé API pour accéder au service d'IA
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">

            {true && (
              <div className="space-y-2">
                <Label>Clé API personnalisée</Label>
                <div className="relative">
                  <Input
                    type={showKey ? 'text' : 'password'}
                    value={config.api_key}
                    onChange={(e) => setConfig(prev => ({ ...prev, api_key: e.target.value }))}
                    placeholder={hasCustomKey ? '••••••••••••••••••••' : 'sk-...'}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2"
                    onClick={() => setShowKey(!showKey)}
                  >
                    {showKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                {hasCustomKey && (
                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                    <CheckCircle className="h-3 w-3 text-green-500" />
                    Une clé personnalisée est configurée
                  </p>
                )}
              </div>
            )}

            
          </CardContent>
        </Card>

        {/* Info Card */}
        <Card className="bg-blue-500/10 border-blue-500/20">
          <CardContent className="pt-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5" />
              <div className="text-sm">
                <p className="font-medium text-blue-500">Information</p>
                <p className="text-muted-foreground">
                  La configuration IA est utilisée pour générer les pages d'inscription 
                  et les posts réseaux sociaux dans le module Sessions de Formation.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Save Button */}
        <div className="flex justify-end">
          <Button onClick={handleSave} disabled={saving}>
            {saving ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Save className="h-4 w-4 mr-2" />
            )}
            Enregistrer
          </Button>
        </div>
      </div>
    </AppLayout>
  );
}
